import { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../lib/firebase';
import { CollectionPaths, TournamentMatchDocument } from 'shared_schemas';
// import { useAuth } from '../../contexts/AuthContext';
import { MatchReportModal } from './MatchReportModal';

interface Props {
    eventId: string;
    startggEventId?: string;
    isAdmin?: boolean;
    playerId?: string; // If provided, highlights matches for this player
}

export function MatchList({ eventId, startggEventId, isAdmin }: Props) {
    const [matches, setMatches] = useState<TournamentMatchDocument[]>([]);
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);
    // const { userDoc } = useAuth(); // kept for potential future role checks
    const [selectedMatch, setSelectedMatch] = useState<TournamentMatchDocument | null>(null);

    // Listen for matches
    useEffect(() => {
        const q = query(
            collection(db, CollectionPaths.tournamentMatches),
            where('eventId', '==', eventId)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const docs = snapshot.docs.map(d => d.data() as TournamentMatchDocument);
            // Client-side sort: Pending/Open first, then by Identifier
            docs.sort((a, b) => {
                const statusOrder: Record<string, number> = { 'in_progress': 0, 'open': 1, 'pending': 2, 'completed': 3, 'cancelled': 4 };
                const sA = statusOrder[a.status] ?? 99;
                const sB = statusOrder[b.status] ?? 99;
                if (sA !== sB) return sA - sB;
                return (a.startggIdentifier || '').localeCompare(b.startggIdentifier || '');
            });
            setMatches(docs);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [eventId]);

    const handleSync = async () => {
        if (!startggEventId) return;
        setSyncing(true);
        const syncFunc = httpsCallable(functions, 'syncEventMatches');
        try {
            await syncFunc({ eventId, startggEventId });
        } catch (error) {
            console.error('Sync failed', error);
            alert('Failed to sync matches from Start.gg');
        } finally {
            setSyncing(false);
        }
    };

    if (loading) return <div className="p-4 text-center text-secondary-400">Loading matches...</div>;

    // Filter for player view if needed (placeholder logic for now)
    const displayMatches = matches;

    return (
        <>
            <div className="space-y-6">
                {isAdmin && (
                    <div className="flex justify-between items-center bg-secondary-900/50 p-4 rounded-lg border border-secondary-700">
                        <div>
                            <h3 className="text-white font-bold">Tournament Matches</h3>
                            <p className="text-sm text-secondary-400">
                                {matches.length} matches tracked
                            </p>
                        </div>
                        <button
                            onClick={handleSync}
                            disabled={syncing || !startggEventId}
                            className="btn-primary flex items-center gap-2"
                        >
                            {syncing ? 'Syncing...' : '↻ Sync from Start.gg'}
                        </button>
                    </div>
                )}

                <div className="space-y-2">
                    {displayMatches.length === 0 && (
                        <div className="text-center py-8 text-secondary-400 border border-dashed border-secondary-700 rounded-lg">
                            No matches found. {isAdmin && "Sync to pull from Start.gg."}
                        </div>
                    )}

                    {displayMatches.map(match => (
                        <div key={match.id} className="card p-3 flex flex-col md:flex-row gap-4 items-center justify-between hover:bg-secondary-800/80 transition-colors">
                            <div className="flex items-center gap-3 w-full md:w-auto">
                                <span className="text-xs text-secondary-500 font-mono w-8">{match.startggIdentifier}</span>
                                <div className="flex flex-col gap-1 w-full md:w-48">
                                    <div className={`flex justify-between items-center ${match.winnerId === match.player1Id ? 'text-green-400 font-bold' : 'text-white'}`}>
                                        <span>{match.player1Name}</span>
                                        <span className="bg-secondary-900 px-2 rounded">{match.player1Score ?? '-'}</span>
                                    </div>
                                    <div className={`flex justify-between items-center ${match.winnerId === match.player2Id ? 'text-green-400 font-bold' : 'text-white'}`}>
                                        <span>{match.player2Name}</span>
                                        <span className="bg-secondary-900 px-2 rounded">{match.player2Score ?? '-'}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="flex items-center gap-4 w-full md:w-auto justify-between md:justify-end">
                                <div className="text-right">
                                    <div className="text-xs text-secondary-400 uppercase tracking-wider font-bold mb-1">
                                        {match.fullRoundText}
                                    </div>
                                    <div className={`text-xs px-2 py-1 rounded inline-block
                                        ${match.status === 'in_progress' ? 'bg-blue-500/20 text-blue-400' :
                                            match.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                                                'bg-secondary-700 text-secondary-400'}
                                    `}>
                                        {match.status.replace('_', ' ')}
                                    </div>
                                </div>

                                <button
                                    onClick={() => setSelectedMatch(match)}
                                    className="btn-secondary text-sm py-1 px-3"
                                >
                                    Report
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {selectedMatch && (
                <MatchReportModal
                    open={true}
                    match={selectedMatch}
                    onClose={() => setSelectedMatch(null)}
                />
            )}
        </>
    );
}
